import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

function resolveLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function DistributorProfileBrand() {
  const { tenant, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/distributor/profile");
        setForm({
          company_name: data?.branding?.company_name || data?.name || "",
          logo_url: data?.branding?.logo_url || "",
          primary_color: data?.branding?.primary_color || "#C85A32",
          secondary_color: data?.branding?.secondary_color || "#8F9E8B",
          slug: data?.slug || "",
          address: data?.address || "",
          tax_id: data?.tax_id || "",
          contact_email: data?.contact_email || "",
          contact_phone: data?.contact_phone || "",
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load profile");
      }
    })();
  }, [tenant?.id]);

  if (!form) return <div className="p-4 sm:p-6 lg:p-8">Loading…</div>;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/distributor/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/distributor/profile", form);
      toast.success("Profile updated");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 space-y-6 max-w-4xl" data-testid="distributor-branding">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Profile &amp; brand</h1>
        <p className="text-muted-foreground">Customize how your distributor portal looks for your team and dealers.</p>
      </div>

      <div className="surface-card p-6 grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="label-overline">Identity</div>
          <div className="space-y-2"><Label>Company name</Label><Input value={form.company_name} onChange={onChange("company_name")} data-testid="dist-branding-company-input" /></div>
          <div className="space-y-2"><Label>Portal slug</Label><Input value={form.slug} onChange={onChange("slug")} data-testid="dist-branding-slug-input" /></div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              {form.logo_url ? (
                <img src={resolveLogoUrl(form.logo_url)} alt="logo" className="w-16 h-16 rounded-md object-cover border border-border" data-testid="dist-branding-logo-preview" />
              ) : (
                <div className="w-16 h-16 rounded-md grid place-items-center border border-dashed border-border text-muted-foreground text-xs">No logo</div>
              )}
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onUploadLogo} className="hidden" data-testid="dist-branding-logo-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dist-branding-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading</> : <><Upload className="w-4 h-4 mr-1" /> Upload logo</>}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">PNG/JPG/SVG/WebP up to 2MB.</div>
            <Input value={form.logo_url} onChange={onChange("logo_url")} placeholder="or paste a URL" data-testid="dist-branding-logo-input" className="mt-2" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Primary color</Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={form.primary_color} onChange={onChange("primary_color")} className="w-12 h-10 rounded-md border border-border" data-testid="dist-branding-primary-color" />
                <Input value={form.primary_color} onChange={onChange("primary_color")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Secondary color</Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={form.secondary_color} onChange={onChange("secondary_color")} className="w-12 h-10 rounded-md border border-border" data-testid="dist-branding-secondary-color" />
                <Input value={form.secondary_color} onChange={onChange("secondary_color")} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="label-overline">Business details</div>
          <div className="space-y-2"><Label>Address</Label><Textarea rows={3} value={form.address} onChange={onChange("address")} data-testid="dist-branding-address-input" /></div>
          <div className="space-y-2"><Label>Tax / GSTIN</Label><Input value={form.tax_id} onChange={onChange("tax_id")} data-testid="dist-branding-tax-input" /></div>
          <div className="space-y-2"><Label>Contact email</Label><Input value={form.contact_email} onChange={onChange("contact_email")} data-testid="dist-branding-email-input" /></div>
          <div className="space-y-2"><Label>Contact phone</Label><Input value={form.contact_phone} onChange={onChange("contact_phone")} data-testid="dist-branding-phone-input" /></div>
        </div>
      </div>

      <div className="surface-card p-6">
        <div className="label-overline mb-3">Live preview</div>
        <div className="flex items-center gap-3">
          {form.logo_url
            ? <img src={resolveLogoUrl(form.logo_url)} alt="logo" className="w-12 h-12 rounded-md object-cover border border-border" />
            : <div className="w-12 h-12 rounded-md grid place-items-center font-display font-black text-white" style={{ background: form.primary_color }}>{(form.company_name || "D").slice(0,1)}</div>}
          <div>
            <div className="font-display font-black text-lg" style={{ color: form.primary_color }}>{form.company_name}</div>
            <div className="label-overline">powered by TileSphere</div>
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="dist-branding-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
    </div>
  );
}
